import { useSyncExternalStore } from 'react';

import { logger, monitoring } from 'common/services';

const PIP_WIDTH = 320;
const PIP_HEIGHT = 480;

/**
 * How the window was opened: on its own when the share started, or by the host from the toast or the
 * Present menu. Split so RUM shows which route Firefox hosts, who never get `auto`, actually find.
 */
export type PipTrigger = 'auto' | 'toast' | 'menu';

/**
 * Why the window closed. A close the code did not explain (`closeScreenSharePip`) is the host's own:
 * the window's close button, or leaving the page.
 */
export type PipCloseReason = 'host' | 'share_ended' | 'crashed';

/**
 * The one floating window this tab may hold. The browser allows a single Document Picture-in-Picture
 * window per tab, so module state mirrors that limit rather than pretending each caller owns one.
 */
let current: { window: Window; trigger: PipTrigger; openedAt: number } | null = null;
let pendingOpen: Promise<Window | null> | null = null;
/**
 * Bumped by every close, so an open that was in flight when the share ended knows it is stale. A
 * counter rather than a flag: a new share can start before the stale request settles, and must get
 * a request of its own rather than reuse the cancelled one.
 */
let openGeneration = 0;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => current?.window ?? null;

/**
 * Forgets the window and records why it went, once per window: from `closeScreenSharePip` with the
 * code's reason, or from `pagehide` as the host's own close if the code did not explain it first.
 * How long hosts keep it, and whether they close it themselves, says whether it is useful.
 */
const recordClosed = (reason: PipCloseReason) => {
  if (!current) return;
  const { trigger, openedAt } = current;
  current = null;
  monitoring.trackAction('broadcast.screen_share_pip.closed', {
    trigger,
    reason,
    openForMs: Math.round(performance.now() - openedAt),
  });
  notify();
};

export const isDocumentPipSupported = () => !!window.documentPictureInPicture;

/**
 * A Document PiP window opens as an empty document. Link tags are cloned as-is; style tags are
 * re-serialised from their CSSOM, because a production emotion sheet inserts rules with `insertRule`
 * and leaves its text node empty. Emotion rules added AFTER this copy are handled by the PiP-scoped
 * emotion cache in `ScreenSharePip`, not here.
 */
const copyDocumentStyles = (target: Document) => {
  // The PiP document has no URL of its own, so root-relative font and image URLs need a base.
  const base = target.createElement('base');
  base.href = window.location.origin;
  target.head.append(base);

  document.head.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    if (node instanceof HTMLLinkElement) {
      target.head.append(node.cloneNode(true));
      return;
    }

    const style = target.createElement('style');
    try {
      const rules = (node as HTMLStyleElement).sheet?.cssRules;
      style.textContent = rules ? Array.from(rules, (rule) => rule.cssText).join('\n') : node.textContent;
    } catch (error) {
      // A cross-origin sheet hides its rules; fall back to the tag's text, and leave a trace.
      logger.warn('[ScreenSharePip] Could not read a stylesheet into the floating window', { error });
      style.textContent = node.textContent;
    }
    target.head.append(style);
  });

  // `visual-refresh` lives on <html> and switches the global font and tokens.
  target.documentElement.className = document.documentElement.className;
  target.body.style.margin = '0';
};

/**
 * Opens the floating window. Must run inside a user activation: a click, or the moment the
 * screen-share picker resolves (approving `getDisplayMedia` grants one in Chromium). Resolves to
 * `null` when the browser refuses, so the caller can offer it another way (the toast, and the Present
 * menu's "Open floating window" row).
 */
export const openScreenSharePip = (trigger: PipTrigger): Promise<Window | null> => {
  if (current) return Promise.resolve(current.window);
  if (pendingOpen) return pendingOpen;

  const api = window.documentPictureInPicture;
  if (!api) {
    monitoring.trackAction('broadcast.screen_share_pip.unsupported');
    return Promise.resolve(null);
  }

  const generation = openGeneration;
  const request: Promise<Window | null> = api
    .requestWindow({ width: PIP_WIDTH, height: PIP_HEIGHT })
    .then((opened) => {
      if (generation !== openGeneration) {
        opened.close();
        return null;
      }
      copyDocumentStyles(opened.document);
      opened.addEventListener('pagehide', () => {
        if (current?.window === opened) recordClosed('host');
      });
      current = { window: opened, trigger, openedAt: performance.now() };
      monitoring.trackAction('broadcast.screen_share_pip.opened', { trigger });
      notify();
      return opened;
    })
    .catch((error: unknown) => {
      // Expected when there was no click to open from (NotAllowedError, always in Firefox); the toast
      // and the Present menu cover it.
      logger.warn('[ScreenSharePip] Could not open the floating window', { trigger, error });
      monitoring.trackAction('broadcast.screen_share_pip.blocked', {
        trigger,
        reason: error instanceof Error ? error.name : 'unknown',
      });
      return null;
    })
    .finally(() => {
      if (pendingOpen === request) pendingOpen = null;
    });

  pendingOpen = request;
  return request;
};

/** Also cancels an open still in flight, so a share that ends first never leaves a window behind. */
export const closeScreenSharePip = (reason: Exclude<PipCloseReason, 'host'>) => {
  openGeneration += 1;
  pendingOpen = null;
  const closing = current?.window;
  recordClosed(reason);
  closing?.close();
};

export const usePipWindow = () => useSyncExternalStore(subscribe, getSnapshot, () => null);
